import { Component, Host, h, Prop, State, Listen, Event, EventEmitter, AttachInternals } from '@stencil/core';
import { IconName } from '../icon/icons';
import { removeIllegalCharacters, removeWhiteSpace } from './util';
import { EventDetail } from '../../utils';

const pattern = '[0-9]*';

declare global {
  interface Window {
    clipboardData: any;
  }
}
@Component({
  tag: 'anchor-number',
  styleUrl: 'number.scss',
  shadow: true,
  formAssociated: true,
})
export class Number {
  @AttachInternals() internals: ElementInternals;

  /**
   * The value in the number field. Use this to set a value
   */
  @Prop() value: number = null;

  /**
   * The name of the number field
   */
  @Prop() name: string = '';

  /**
   * The input type of the number field
   */
  @Prop() type: string = 'text';

  /**
   * Placeholder text is shown until the user interacts with the number field
   */
  @Prop() placeholder: string = '';

  /**
   * Set the field to be read only
   */
  @Prop() readonly: boolean = false;

  /**
   * Disable the number field
   */
  @Prop() disabled: boolean = false;

  /**
   * Show an error message
   */
  @Prop() errorProp: string = '';

  /**
   * Show a warning message
   */
  @Prop() warning: string = '';

  /**
   * Show a checkmark icon when the number field has been succesfully filled
   */
  @Prop() filled: boolean = false;

  /**
   * Show a help text
   */
  @Prop() helpText: string = '';

  /**
   * Show the label (you can't currently disable the label)
   */
  @Prop() label: boolean = true;

  /**
   * Placement of the label
   */
  @Prop() labelPlacement: 'top' | 'left' = 'top';

  /**
   * Set a fixed width in characters for the number field.
   */
  @Prop() size: number = null;

  /**
   * Set a max width in characters for the number field.
   */
  @Prop() maxLength: number;

  /**
   * Add an info icon with a tooltip to the right of the label
   */
  @Prop() tooltipText: string = '';

  /**
   * Add an icon at the right side of the input field, by putting in the name of an Anchor icon
   */
  @Prop() icon: IconName;

  /**
   * Hide spin button when using `type="number"`.
   */
  @Prop() hideSpinButtons: boolean;

  /**
   * Should the icon be inside or outside the input field
   */
  @Prop() iconPlacement: 'inside' | 'outside' = 'inside';

  @Prop() spinners: 'none' | 'small' | 'big' = 'none';

  /**
   * Event emitted when the user is inputting something into the input field
   */
  @Event() inputevent: EventEmitter<EventDetail>;

  /**
   * Event emitted when the input is changed. Only triggers when focus leaves the input.
   */
  @Event() inputChange: EventEmitter<EventDetail>;

  /**
   * Event emitted when the user is focusing the input field
   */
  @Event() focusevent: EventEmitter;

  /**
   * Event emitted when the user blurs the input field
   */
  @Event() blurevent: EventEmitter;

  /**
   * Event emitted when the user clears the input field
   */
  @Event() clearevent: EventEmitter;

  @State() hasErrorSlotActive: boolean = false;

  @State() prevValue = this.value;

  // eslint-disable-next-line class-methods-use-this
  @Listen('keyup')
  @Listen('input')
  handleSingleInput(ev: any) {
    if (ev.target.value) {
      removeWhiteSpace(ev);
      removeIllegalCharacters(ev, pattern);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  @Listen('paste')
  handlePasteInput(ev: any) {
    const text = (ev.clipboardData || window.clipboardData).getData('text');
    // eslint-disable-next-line no-param-reassign
    ev.target.value = text;

    removeWhiteSpace(ev);
    removeIllegalCharacters(ev, pattern);
  }

  render() {
    const spinnerSubIcon = this.spinners === 'small' ? 'chevron-down' : 'minus';
    const spinnerAddIcon = this.spinners === 'small' ? 'chevron-up' : 'plus';
    return (
      <Host>
        <div class="anchor-c-number">
          {this.spinners !== 'none' && (
            <button class="anchor-c-number__spinner" data-type="subtract" title="Subtract 1" aria-controls="spinner-input">
              <anchor-icon name={spinnerSubIcon} />
            </button>
          )}
          <anchor-input
            class={`input ${this.spinners !== 'none' ? `anchor-c-number--w-${this.spinners}-spinners` : ''}`}
            disabled={this.disabled}
            errorProp={this.errorProp}
            filled={this.filled}
            helpText={this.helpText}
            icon={this.icon}
            iconPlacement={this.iconPlacement}
            inputmode="numeric"
            maxLength={this.maxLength}
            name={this.name}
            type={this.type}
            pattern={pattern}
            placeholder={this.placeholder}
            readonly={this.readonly}
            size={this.size}
            value={this.value}
            hideSpinButtons={this.hideSpinButtons}
          >
            <slot />
          </anchor-input>
          {this.spinners !== 'none' && (
            <button data-type="add" class="anchor-c-number__spinner-add" title="Add 1" aria-controls="spinner-input">
              <anchor-icon name={spinnerAddIcon} />
            </button>
          )}
        </div>
      </Host>
    );
  }
}
